import React, { useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGoogleLogin } from "@react-oauth/google";
import { Mail, Lock, AlertCircle, Eye, EyeOff } from "lucide-react";
import Logo from "@/components/brand/Logo";
import FixyBeeMark from "@/components/brand/FixyBeeMark";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { clearAcquisition, getAcquisition } from "@/lib/acquisition";
import { errorCode, errorMessage } from "@/lib/errors";
import { GoogleMark } from "@/components/auth/SignupParts";
import { Button } from "@/ds";
import { readReturnTo, withReturnTo } from "@/lib/returnTo";

export default function Login() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  // A Google address with no account: the login page never creates one (it
  // has no role to give it), so it points at the two sign-up doors instead.
  const [noAccount, setNoAccount] = useState(false);
  const { loginEmail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const initialError = location.state?.error || "";
  React.useEffect(() => { if (initialError) setError(initialError); }, [initialError]);

  // Where they were headed. Either a CTA said so, or `ProtectedRoute` turned
  // them away from a page and put the address here — which is the same
  // question, and used to be answered by a `state.from` this page never read.
  const returnTo = readReturnTo(searchParams);
  const afterAuth = (data) => {
    if (data.needs_onboarding) {
      // An account from before 2026-10-28, still without a role.
      return withReturnTo("/signup/client", returnTo);
    }
    if (returnTo) return returnTo;
    const role = data.user?.profile?.role;
    if (role === "provider") return "/provider/dashboard";
    if (data.user?.profile?.is_admin) return "/admin";
    return "/client/dashboard";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await loginEmail(email, password);
      navigate(afterAuth(data), { replace: true });
    } catch (e) {
      setError(e?.response?.status === 401 ? t("signupX.loginInvalid")
        : errorMessage(e, t("common.error")));
    } finally {
      setLoading(false);
    }
  };

  const { setUser } = useAuth();

  const handleGoogle = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setError("");
      setNoAccount(false);
      setLoading(true);
      try {
        const acquisition = getAcquisition();
        const { data } = await api.post("/auth/google", {
          access_token: tokenResponse.access_token,
          // T22. Used by the server only if this call creates the account.
          ...(acquisition ? { acquisition } : {}),
          // Seeds user_profiles.language on account CREATION only; ignored for
          // an account that already exists. See _signup_language in
          // routes/auth.py.
          language: String(i18n.language || "").toLowerCase().startsWith("es") ? "es" : "en",
        });
        clearAcquisition();
        setUser(data.user);
        // The Google popup never unloads this page, so `returnTo` is still in
        // its URL when the round-trip returns. Nothing to stash.
        navigate(afterAuth(data), { replace: true });
      } catch (err) {
        if (errorCode(err) === "role_required") setNoAccount(true);
        else setError(errorMessage(err, t("common.error")));
      } finally {
        setLoading(false);
      }
    },
    onError: () => setError(t("auth.googleFail")),
  });

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-cream" data-testid="login-page">
      <div className="hidden md:flex md:w-1/2 bg-worker text-cream relative items-center justify-center p-12 overflow-hidden">
        <div className="absolute top-8 left-8 bg-cream rounded-md px-3 py-2">
          <Logo size={32} />
        </div>
        <div className="relative max-w-md">
          <div className="absolute inset-0 bg-hive/10 rounded-full blur-3xl"></div>
          <div className="relative flex flex-col items-center">
            <div className="animate-bee-wings"><FixyBeeMark size={180} /></div>
            <h2 className="mt-8 font-display text-3xl font-bold text-center text-balance">
              {t("auth.loginTitle")}
            </h2>
            <p className="mt-3 text-cream/70 text-center max-w-sm">
              {t("auth.loginSubtitle")}
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8"><Logo size={36} /></div>
          <h1 className="font-display text-4xl font-bold text-worker">{t("auth.loginBtn")}</h1>
          <p className="mt-2 text-smoke">{t("auth.loginSubtitle")}</p>

          {error && (
            <div className="mt-6 p-4 rounded-sm bg-danger/10 border border-danger/30 text-danger flex gap-2 items-start text-sm" data-testid="login-error">
              <AlertCircle size={18} className="mt-0.5" /> {error}
            </div>
          )}

          {noAccount && (
            <div className="mt-6 p-4 rounded-sm bg-linen border border-sand text-sm text-worker" role="alert" data-testid="login-no-account">
              <p>{t("signupX.noAccountGoogle")}</p>
              <div className="mt-3 flex flex-col gap-2">
                <Link to={withReturnTo("/signup/client", returnTo)} className="font-semibold underline min-h-[44px] inline-flex items-center" data-testid="login-no-account-client">
                  {t("signupX.needService")}
                </Link>
                <Link to={withReturnTo("/signup/provider", returnTo)} className="font-semibold underline min-h-[44px] inline-flex items-center" data-testid="login-no-account-provider">
                  {t("signupX.offerServices")}
                </Link>
              </div>
            </div>
          )}

          <Button
            type="button" variant="secondary" fullWidth leading={<GoogleMark />}
            onClick={() => handleGoogle()} className="mt-8" data-testid="login-google-btn"
          >
            {t("auth.continueGoogle")}
          </Button>

          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-smoke">
            <div className="h-px flex-1 bg-sand"></div> {t("auth.or")} <div className="h-px flex-1 bg-sand"></div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="label-base">{t("auth.email")}</label>
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-smoke" />
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="input-base pl-10" placeholder={t("auth.emailPlaceholder")} data-testid="login-email"
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="label-base">{t("auth.password")}</label>
                <Link to="/forgot-password" className="text-xs text-hive-ink hover:text-worker font-display"
                  data-testid="login-forgot-link">
                  {t("auth.forgot")}
                </Link>
              </div>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-smoke" />
                <input
                  type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="input-base pl-10 pr-12" placeholder="••••••••" data-testid="login-password"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} aria-label={t("auth.password")} className="absolute right-1 top-1/2 -translate-y-1/2 h-11 w-11 inline-flex items-center justify-center text-smoke hover:text-worker" data-testid="login-pw-toggle">
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button type="submit" fullWidth loading={loading} data-testid="login-submit">
              {t("auth.loginBtn")}
            </Button>
          </form>

          <div className="mt-6 text-sm text-center text-smoke">
            <p>{t("auth.noAccount")}</p>
            <div className="mt-2 flex flex-col items-center gap-1">
              <Link to={withReturnTo("/signup/client", returnTo)} className="font-semibold text-worker hover:text-hive-ink underline min-h-[44px] inline-flex items-center" data-testid="login-signup-client">
                {t("signupX.needService")}
              </Link>
              <Link to={withReturnTo("/signup/provider", returnTo)} className="font-semibold text-worker hover:text-hive-ink underline min-h-[44px] inline-flex items-center" data-testid="login-signup-provider">
                {t("signupX.offerServices")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
